import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

interface DateOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  urlsWithCustomExpiry: number;
  urlsWithCustomStart: number;
  totalUrls: number;
  onConfirm: (overrideCustomDates: boolean) => void;
  loading?: boolean;
}

export function DateOverrideDialog({
  open,
  onOpenChange,
  urlsWithCustomExpiry,
  urlsWithCustomStart,
  totalUrls,
  onConfirm,
  loading = false,
}: DateOverrideDialogProps) {
  const hasCustomDates = urlsWithCustomExpiry > 0 || urlsWithCustomStart > 0;

  const handleKeepCustom = () => {
    onConfirm(false); // Don't override custom dates
  };

  const handleOverrideAll = () => {
    onConfirm(true); // Override all dates including custom ones
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <AlertDialogTitle>Custom Dates Detected</AlertDialogTitle>
              <AlertDialogDescription className="mt-2 space-y-3">
                <p>
                  This collection contains <strong>{totalUrls}</strong> URL{totalUrls !== 1 ? 's' : ''}:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {urlsWithCustomExpiry > 0 && (
                    <li>
                      <strong>{urlsWithCustomExpiry}</strong> URL{urlsWithCustomExpiry !== 1 ? 's have' : ' has'} custom expiry dates
                    </li>
                  )}
                  {urlsWithCustomStart > 0 && (
                    <li>
                      <strong>{urlsWithCustomStart}</strong> URL{urlsWithCustomStart !== 1 ? 's have' : ' has'} custom start dates
                    </li>
                  )}
                </ul>
                <p className="font-medium text-foreground">
                  How would you like to proceed?
                </p>
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleKeepCustom();
            }}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Keep Custom Dates
          </AlertDialogAction>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleOverrideAll();
            }}
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700"
          >
            Override All
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
